import {AsyncResult} from 'Core/models/Async';
import {isRight} from 'fp-ts/lib/Either';
import {Map as ImmutableMap} from 'immutable';
import {DateTime} from 'luxon';
import {CompensationProcessingType, PredefinedHierarchicalContracts} from 'Quoting/api/types';
import {ValueListItem} from 'Quoting/components/QuoteForm/Plan/products/types';
import {BROKER_PORTAL_SELF_SERVE_UW_OFFER} from 'Quoting/constants';
import {
  A360Lookup,
  MinMaxResult,
  MinMaxResultValueType,
  PlanWithCoverages,
  QuoteFormDropdownOption,
  RetreivedPlan,
} from 'Quoting/models/DTOs/QuoteForm';
import {A360Plan, A360ProductModel} from 'Quoting/models/Quoting';
import {getProposalGenerationSupportedStates} from 'Quoting/utils/ProposalGenerationSupportedStates';
import {createEisTypeObject, randomUUID} from 'Quoting/utils/utilities';

export const getAvailablePlans = (plansWithCoverages: PlanWithCoverages[], riskStateCd: string): RetreivedPlan[] =>
  Array.isArray(plansWithCoverages)
    ? plansWithCoverages.map(p => ({
        name: p.name,
        description: p.description,
        key: randomUUID(),
        planCode: p.code,
        coverages: p.coverages.map(getCoverage),
        classes: [],
        isDisabled: !p.availability.available,
        error: p.availability?.message,
        propGenSupported: getProposalGenerationSupportedStates(p.code as A360Plan)?.includes(riskStateCd),
      }))
    : [];

const getCoverage = (coverage: PlanWithCoverages['coverages'][0]): RetreivedPlan['coverages'][0] => ({
  name: coverage.name,
  code: coverage.code,
  key: randomUUID(),
  applicability: coverage.applicabilityType,
  benefitType: {},
  contributionType: {},
  rateBasis: {},
  classes: [],
});

export const enrichInitialQuote = (
  quote: any,
  lookupResponse: Array<AsyncResult<{body: {success: A360Lookup[]}}>>,
  gpluDefaults?: any // required for GWL
) => {
  const modelName = quote._modelName;

  switch (modelName) {
    case A360ProductModel.HIMaster:
      return enrichHIQuote(quote, lookupResponse);

    case A360ProductModel.CIMaster:
      return enrichCIQuote(quote, lookupResponse);

    case A360ProductModel.ACCMaster:
      return enrichACCQuote(quote, lookupResponse);

    case A360ProductModel.GPLUMaster:
      return enrichGPLUQuote(quote, lookupResponse, gpluDefaults);

    default:
      return quote;
  }
};

const enrichHIQuote = (quote: any, lookupResponse: Array<AsyncResult<{body: {success: A360Lookup[]}}>>) => {
  const propertiesToAdd = [
    {key: 'commonProvisionDetails', type: 'HICommonProvision'},
    {key: 'enrollmentDetails', type: 'HIEnrollmentDetail'},
    {key: 'transactionDetails', type: 'HITransactionDetails'},
    {key: 'participationDetails', type: 'HIEligibleLivesAndParticipation'},
    {key: 'policyDetail', type: 'HIPolicyDetail'},
  ];

  propertiesToAdd.forEach(({key, type}) => {
    quote[key] = quote[key] ?? createEisTypeObject(type, quote._key?.rootId, quote._key?.rootId);
  });

  if (quote.isPriorCoverage) {
    quote.priorCarrierDetails =
      quote.priorCarrierDetails ?? createEisTypeObject('HIPriorCarrierInfo', quote._key?.rootId, quote._key?.rootId);
  }

  if (!quote.underwritingDetail) {
    quote.underwritingDetails = createEisTypeObject('HIUnderwriterInfo', quote._key?.rootId, quote._key?.rootId);
    if (isRight(lookupResponse[0])) {
      quote.underwritingDetails.underwritingCompany = lookupResponse[0].right?.body?.success[0]?.code;
    }
  }

  if (!quote.sicDescription && isRight(lookupResponse[1])) {
    const sicDescription =
      lookupResponse[1].right?.body?.success?.find(lookup => lookup.code === quote.sicCode)?.displayValue ?? '';
    quote.sicDescription = sicDescription;
  }

  if (!quote.rateRequestDate) {
    quote.rateRequestDate = quote.originalRateRequestDate ?? quote?.transactionDetails?.txCreateDate;
  }

  quote.classGroups = quote.classGroups ?? [];

  quote.policyDetail[
    quote._modelName === A360ProductModel.HIMaster ? 'brokerUWOffer' : 'quoteUWOffer'
  ] = BROKER_PORTAL_SELF_SERVE_UW_OFFER;

  if (!quote.rateGuaranteeRenewalDetails) {
    const rateGuaranteeMonths = 24;
    const nextRenewalEffectiveDate = DateTime.fromISO(quote.termDetails.termEffectiveDate)
      .plus({months: quote.rateGuaranteeMonths ?? 24})
      .toISODate();
    const nextRenewalQuoteStartDate = DateTime.fromISO(nextRenewalEffectiveDate)
      .minus({months: 3})
      .toISO();

    quote.rateGuaranteeRenewalDetails = {
      rateGuaranteeMonths: rateGuaranteeMonths,
      nextRenewalEffectiveDate: nextRenewalEffectiveDate,
      nextRenewalQuoteStartDate: nextRenewalQuoteStartDate,
      ...createEisTypeObject('HIRateGuaranteeRenewalInfo', quote._key?.rootId, quote._key?.rootId),
    };

    quote.deliveryModelCd = 'GroupOrVoluntary';
  }

  return quote;
};

export const enrichCIQuote = (quote: any, lookupResponse: Array<AsyncResult<{body: {success: A360Lookup[]}}>>) => {
  const propertiesToAdd = [
    {key: 'commonProvisionDetails', type: 'CICommonProvision'},
    {key: 'enrollmentDetails', type: 'CIEnrollmentDetail'},
    {key: 'transactionDetails', type: 'CITransactionDetails'},
    {key: 'participationDetails', type: 'CIEligibleLivesAndParticipation'},
    {key: 'policyDetail', type: 'CIPolicyDetail'},
  ];

  propertiesToAdd.forEach(({key, type}) => {
    quote[key] = quote[key] ?? createEisTypeObject(type, quote._key?.rootId, quote._key?.rootId);
  });

  if (quote.isPriorCoverage) {
    quote.priorCarrierDetails =
      quote.priorCarrierDetails ?? createEisTypeObject('CIPriorCarrierInfo', quote._key?.rootId, quote._key?.rootId);
  }

  if (!quote.underwriterDetails) {
    quote.underwriterDetails = createEisTypeObject('CIUnderwriterInfo', quote._key?.rootId, quote._key?.rootId);
    if (isRight(lookupResponse[0])) {
      quote.underwriterDetails.underwritingCompany = lookupResponse[0].right?.body?.success[0]?.code;
    }
  }

  if (!quote.sicDescription && isRight(lookupResponse[1])) {
    const sicDescription =
      lookupResponse[1].right?.body?.success?.find(lookup => lookup.code === quote.sicCode)?.displayValue ?? '';
    quote.sicDescription = sicDescription;
  }

  if (!quote.rateRequestDate) {
    quote.rateRequestDate = quote.originalRateRequestDate ?? quote?.transactionDetails?.txCreateDate;
  }

  quote.classGroups = quote.classGroups ?? [];

  quote.policyDetail.quoteUWOffer = BROKER_PORTAL_SELF_SERVE_UW_OFFER;

  if (!quote.rateGuaranteeRenewalDetails) {
    const rateGuaranteeMonths = 24;
    const nextRenewalEffectiveDate = DateTime.fromISO(quote.termDetails.termEffectiveDate)
      .plus({months: quote.rateGuaranteeMonths ?? 24})
      .toISODate();
    const nextRenewalQuoteStartDate = DateTime.fromISO(nextRenewalEffectiveDate)
      .minus({months: 3})
      .toISO();

    quote.rateGuaranteeRenewalDetails = {
      rateGuaranteeMonths: rateGuaranteeMonths,
      nextRenewalEffectiveDate: nextRenewalEffectiveDate,
      nextRenewalQuoteStartDate: nextRenewalQuoteStartDate,
      ...createEisTypeObject('CIRateGuaranteeRenewalInfo', quote._key?.rootId, quote._key?.rootId),
    };

    quote.deliveryModelCd = 'GroupOrVoluntary';
    quote.individualRecordType = 'Member';
    quote.offerStatus = 'offered';
    quote.rateEffectiveDate = quote?.termDetails?.termEffectiveDate;
  }

  return quote;
};

export const enrichACCQuote = (quote: any, lookupResponse: Array<AsyncResult<{body: {success: A360Lookup[]}}>>) => {
  const propertiesToAdd = [
    {key: 'transactionDetails', type: 'ACCTransactionDetails'},
    {key: 'participationDetails', type: 'ACCEligibleLivesAndParticipation'},
    {key: 'commonProvisionDetails', type: 'ACCCommonProvision'},
    {key: 'enrollmentDetails', type: 'ACCEnrollmentDetail'},
    {key: 'policyDetail', type: 'ACCPolicyDetail'},
  ];

  propertiesToAdd.forEach(({key, type}) => {
    quote[key] = quote[key] ?? createEisTypeObject(type, quote._key?.rootId, quote._key?.rootId);
  });

  if (quote.isPriorCoverage) {
    quote.priorCarrierDetails =
      quote.priorCarrierDetails ?? createEisTypeObject('ACCPriorCarrierInfo', quote._key?.rootId, quote._key?.rootId);
  }

  if (!quote.underwritingDetail) {
    quote.underwritingDetails = createEisTypeObject('ACCUnderwriterInfo', quote._key?.rootId, quote._key?.rootId);
    if (isRight(lookupResponse[0])) {
      quote.underwritingDetails.underwritingCompany = lookupResponse[0].right?.body?.success[0]?.code;
    }
  }

  if (!quote.sicDescription && isRight(lookupResponse[1])) {
    const sicDescription =
      lookupResponse[1].right?.body?.success?.find(lookup => lookup.code === quote.sicCode)?.displayValue ?? '';
    quote.sicDescription = sicDescription;
  }

  if (!quote.rateRequestDate) {
    quote.rateRequestDate = quote.originalRateRequestDate ?? quote?.transactionDetails?.txCreateDate;
  }

  quote.classGroups = quote.classGroups ?? [];

  quote.policyDetail.quoteUWOffer = BROKER_PORTAL_SELF_SERVE_UW_OFFER;

  if (!quote.rateGuaranteeRenewalDetails) {
    const rateGuaranteeMonths = 24;
    const nextRenewalEffectiveDate = DateTime.fromISO(quote.termDetails.termEffectiveDate)
      .plus({months: quote.rateGuaranteeMonths ?? 24})
      .toISODate();
    const nextRenewalQuoteStartDate = DateTime.fromISO(nextRenewalEffectiveDate)
      .minus({months: 3})
      .toISO();

    quote.rateGuaranteeRenewalDetails = {
      rateGuaranteeMonths: rateGuaranteeMonths,
      nextRenewalEffectiveDate: nextRenewalEffectiveDate,
      nextRenewalQuoteStartDate: nextRenewalQuoteStartDate,
      ...createEisTypeObject('ACCRateGuaranteeRenewalInfo', quote._key?.rootId, quote._key?.rootId),
    };

    quote.deliveryModelCd = 'GroupOrVoluntary';
    quote.individualRecordType = 'Member';
    quote.offerStatus = 'offered';
    quote.rateEffectiveDate = quote?.termDetails?.termEffectiveDate;
  }

  return quote;
};

export const enrichGPLUQuote = (
  quote: any,
  lookupResponse: Array<AsyncResult<{body: {success: A360Lookup[]}}>>,
  gpluDefaults?: any
) => {
  if (!quote.sicDescription && isRight(lookupResponse[0])) {
    const sicDescription =
      lookupResponse[0].right?.body?.success?.find(lookup => lookup.code === quote.sicCode)?.displayValue ?? '';
    quote.sicDescription = sicDescription;
  }

  quote.classGroups = quote.classGroups ?? [];
  quote.policyDetail.quoteUWOffer = BROKER_PORTAL_SELF_SERVE_UW_OFFER;

  if (gpluDefaults) {
    quote.policyDetail.maturityAge = gpluDefaults.maturityAge;
    quote.policyDetail.doliMaturAge = gpluDefaults.doliMaturAge;
  }

  if (!quote.detailEnrollment) {
    quote.detailEnrollment = {
      ...createEisTypeObject('GPLUEnrollmentDetail', quote._key?.rootId, quote._key?.rootId),
    };
  }

  return quote;
};

export const updateRetrievedValues = (
  retrievedValuesMap: ImmutableMap<string, QuoteFormDropdownOption[]> | undefined,
  planCd: string | undefined
) => {
  switch (planCd) {
    case 'GHI3': {
      if (ImmutableMap.isMap(retrievedValuesMap)) {
        const preExistingConditionLimitationPeriodCdValueList:
          | QuoteFormDropdownOption[]
          | undefined = ImmutableMap.isMap(retrievedValuesMap)
          ? retrievedValuesMap.get(
              'HIPreExLimitationAndPregnancyWaitingPeriod.preExistingConditionLimitationPeriodCd'
            ) ?? []
          : [];

        if (Array.isArray(preExistingConditionLimitationPeriodCdValueList)) {
          preExistingConditionLimitationPeriodCdValueList?.push({
            value: 'None',
            label: 'None',
          });
          return retrievedValuesMap.set(
            'HIPreExLimitationAndPregnancyWaitingPeriod.preExistingConditionLimitationPeriodCd',
            preExistingConditionLimitationPeriodCdValueList
          );
        }
      }

      return retrievedValuesMap ?? ImmutableMap();
    }
    default:
      return retrievedValuesMap ?? ImmutableMap();
  }
};

export const enrichQuoteAfterOfferCall = (quote: Record<string, any>) => {
  quote?.benefitPackagingDetail?.plans?.forEach(plan => {
    if (
      (plan?.planCd === 'GCI5' || plan?.planCd === 'GVCI4') &&
      plan.coverageDefs[0].benefitSchedule.isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied
    ) {
      //update to a loop if/when buyups comes into picture
      plan.coverageDefs[0].benefitSchedule.specifiedChronicIllnessAndAccidentalInjuryRider.injuryPct = [
        'MI',
        'OR',
      ].includes(quote?.riskStateCd)
        ? 0
        : 2 *
          plan?.coverageDefs?.[0]?.benefitSchedule?.specifiedChronicIllnessAndAccidentalInjuryRider
            ?.planBenefitAmountPct;
    }
  });
  return quote;
};

export const createOptionsForLookup = (options: A360Lookup[]): QuoteFormDropdownOption[] =>
  options.map(opt => ({label: opt.displayValue, value: opt.code}));

export const createOptionsForAttributes = (options: ValueListItem[]): QuoteFormDropdownOption[] =>
  options.map(opt => {
    const valueKey = `${opt?.itemValue?.valueType?.toLowerCase()}Value` as keyof typeof opt.itemValue;
    return {
      label: opt.label,
      value: String(opt.itemValue[valueKey] ?? ''),
    };
  });

export const getValueFromMinMaxResult = (minMaxResult: MinMaxResult) => {
  const valueType = minMaxResult?.result?.valueType;
  const value: any = minMaxResult?.result?.[`${valueType?.toLowerCase()}Value`];

  switch (valueType) {
    case MinMaxResultValueType.Money:
      return value?.['amount'];
    case MinMaxResultValueType.Decimal:
      return !Number.isNaN(value) && Number.isFinite(value) ? value * 100 : value;
    default:
      return value;
  }
};

export const getErrorMessageFromMinMaxResult = (message: string, minMaxResult: MinMaxResult) => {
  const valueType = minMaxResult?.result?.valueType;

  if (valueType === MinMaxResultValueType.Decimal) {
    return message.replace(/\$\{"(.*?)"\}/g, (match, value) => {
      const val = Number(value);
      return Number.isFinite(val) ? String(val * 100) : match;
    });
  }

  return message?.replace(/\$\{"(.*?)"\}/g, '$1');
};

export const getQuotedCompensationRates = (predefinedHierarchicalContracts: PredefinedHierarchicalContracts) => {
  const compensationRule =
    predefinedHierarchicalContracts?.contractResolutionResponse?.[0]?.contracts?.[0]?.processingDefinition
      ?.calculationDefinition?.rule;

  if (compensationRule?.processingType === CompensationProcessingType.FlatPercent) {
    return {
      processingType: compensationRule?.processingType,
      quotedCommissionRate: compensationRule?.compensationRate?.rate,
    };
  } else if (compensationRule?.processingType === CompensationProcessingType.IndividualHeapedPercent) {
    return {
      processingType: compensationRule?.processingType,
      quotedCommissionFYRate: compensationRule?.bounds?.[0]?.rate,
      quotedCommissionRenewalRate: compensationRule?.bounds?.[1]?.rate,
    };
  } else {
    return null;
  }
};
